import React from "react";
import {connect} from "react-redux";
import {FormattedMessage} from "react-intl";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";

import {postRegenerateLookAiImage} from "../../aiApi/aiRegenerateApi";
import {setLookAICustomPrompt, setLookAiLimitBoxOpened} from "../../aiActions";
import {isAiRenderLimitReached} from "../../aiConfig";
import {getSignal, newController} from "../../aiApi/aiController";
import {getCommonVisualizerType} from "../../../../config";
import COLOR_THEMES from "../../../../colorThemes";
import {useAppSelector} from "../../../../storeHooks";

// The custom prompt block revealed at the bottom of the AI image overlay when the user starts a
// free-text edit of the result on screen. It is not tied to multiImage results any more - the
// edit mode is entered either from the "edit" button of one of the pieces of a multiImage result
// (see LookAIMultiImage / AIResultCustomPromptButtons) or from the "edit" button rendered next to
// "download" for every other result (see LookAIImageOverlayImageContent). The block is 80px high
// and slides in from below, while the image area above shrinks by the same amount.
// The textarea and the button are both kept at the height of LookAIPopupGenerateButton
// (.look-button / .ui-button2 + u-h-25 = 2.5rem).
//
// "Regenerate" does NOT go through the /start-convert + poll flow of the generate button: it
// posts the image being edited (the cut piece of a multiImage result, or the whole result image)
// plus the typed prompt to the dedicated edit-image endpoint and swaps the returned image back
// in - see postRegenerateLookAiImage in aiApi/aiRegenerateApi.

export const AI_EDIT_BLOCK_HEIGHT = 80; // px, keep in sync with .look-ai__overlay-edit-block

type Props = {
  opened: boolean,
  lookAICustomPrompt: string,
  aiLoading: boolean,
  aiRegenerateLoading: boolean,
  regenerateError: string,
  aiResults: any[],
  setLookAiLimitBoxOpened: (lookAiLimitBoxOpened: boolean) => void,
  setLookAICustomPrompt: (lookAICustomPrompt: string) => void,
  postRegenerateLookAiImage: (signal?: AbortSignal) => void,
};

const AIResultCustomPrompt = ({
  opened,
  lookAICustomPrompt,
  aiLoading,
  aiRegenerateLoading,
  regenerateError,
  aiResults,
  setLookAiLimitBoxOpened,
  setLookAICustomPrompt,
  postRegenerateLookAiImage,
}: Props) => {

  const {activeTheme} = useAppSelector(state => state.serverState);

  const disabled = aiLoading || aiRegenerateLoading
    || !lookAICustomPrompt || !lookAICustomPrompt.trim();

  const onRegenerateClick = () => {
    if (disabled) {
      return;
    }
    if (isAiRenderLimitReached(aiResults)) {
      setLookAiLimitBoxOpened(true);
      return;
    }
    const controller = newController(); // replace the shared controller
    postRegenerateLookAiImage(controller.signal ?? getSignal());
  };

  const visualizerType = getCommonVisualizerType();

  return (
    <div style={{height: AI_EDIT_BLOCK_HEIGHT}}
         className={"look-ai__overlay-edit-block flex !items-end u-gap-05 u-p-05"
           + (opened ? " look-ai__overlay-edit-block--opened" : "")}>
      <div className="flex--1">
        <div className="">
          <InputLabel shrink
                      htmlFor="lookAICustomPrompt"
                      sx={{
                        color: COLOR_THEMES[activeTheme].primary,
                        transform: "none",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        marginBottom: "2px",
                      }}>
            {regenerateError
              ? <span className="look-ai__overlay-edit-error">{regenerateError}</span>
              : <FormattedMessage id={"look.ai.overlay.customPromptPlaceholder"} values={{visualizerType}}/>}
          </InputLabel>
          <TextField id="lookAICustomPrompt"
                     size="small"
                     variant="outlined"
                     fullWidth={true}
                     value={lookAICustomPrompt}
                     multiline
                     maxRows={2}
                     disabled={aiRegenerateLoading}
                     onChange={(event) => setLookAICustomPrompt(event.target.value)}
                     sx={{
                       "& .MuiOutlinedInput-notchedOutline": {
                         top: 0,
                       },
                       "& .MuiOutlinedInput-notchedOutline > legend": {
                         display: "none",
                       },
                     }}
          />
        </div>
      </div>
      <Button className={"nowrap look-ai__overlay-edit-button ui-button4 ui-button4--primary look-button look-button--primary u-rounded-6 u-pr-125 u-pl-125 !text-[12px]"}
              disabled={disabled}
              onClick={onRegenerateClick}>
        <FormattedMessage id="look.ai.overlay.regenerateButton"/>
      </Button>
    </div>
  );
};

const mapStateToProps = (store: any) => {
  const {lookAICustomPrompt, aiLoading, aiRegenerateLoading, lookAIEditPiece, aiResults} = store.aiState;

  return {
    lookAICustomPrompt,
    aiLoading,
    aiRegenerateLoading,
    aiResults,
    regenerateError: (lookAIEditPiece && lookAIEditPiece.regenerateError) || "",
  };
};

const mapDispatchToProps = {
  setLookAICustomPrompt,
  setLookAiLimitBoxOpened,
  postRegenerateLookAiImage,
};

export default connect(mapStateToProps, mapDispatchToProps)(AIResultCustomPrompt)
